import { apiClient } from './client';

/**
 * 증명서 발급 API — backend `modules/hr/certificate.routes.ts` (인사, migration 129).
 *
 * ★ **재직증명서는 신청이 곧 발급**이다. 그래서 `create` 의 응답이 이미 `status: 'ISSUED'` 이고
 *   발급번호가 붙어 있다 — 화면은 그 결과를 보고 «바로 받기»와 «신청 접수»를 가른다.
 * ★ **옛 서버에는 이 라우트가 없다**(404). 부르는 쪽이 실패를 삼키고 화면을 접는다.
 */

export type CertKind = 'EMPLOYMENT' | 'CAREER';
export type CertStatus = 'REQUESTED' | 'ISSUED' | 'REJECTED';

export interface CertificateDto {
  id: string;
  userId: string;
  /** 담당자 목록에서만 채워진다(내 이력에서는 null — 나 자신이라 필요 없다). */
  userName: string | null;
  kind: CertKind;
  kindLabel: string;
  status: CertStatus;
  purpose: string | null;
  recipient: string | null;
  copies: number;
  maskRrn: boolean;
  /** 발급되어야 생긴다. `HR-2026-0001`. */
  docNo: string | null;
  issuedAt: string | null;
  rejectReason: string | null;
  createdAt: string;
}

/** 인쇄할 내용 — 저장된 파일이 아니라 **부를 때마다 다시 만들어지는** 값이다. */
export interface CertificateDocumentDto {
  kind: CertKind;
  kindLabel: string;
  docNo: string | null;
  issuedAt: string | null;
  companyName: string;
  representativeName: string | null;
  /**
   * ★★ 사업장 주소 (2026-08-30 · 판 A1) — 그 사람의 **근무지 주소**, 없으면 회사 주소.
   * ★ 아무 데도 없으면 `null` 이고, 화면은 **줄을 안 그린다**(빈 줄은 지운 것처럼 보인다).
   * ★ 옛 서버는 이 칸을 안 준다 → 없으면 그냥 안 그린다.
   */
  address?: string | null;
  /** 주소가 근무지에서 왔으면 그 이름(「부산지사」). 회사 주소면 null. */
  worksiteName?: string | null;
  name: string;
  employeeNumber: string | null;
  belong: string;
  position: string;
  hireDate: string | null;
  period: string;
  tenure: string | null;
  purpose: string | null;
  recipient: string | null;
  copies: number;
  maskRrn: boolean;
  statement: string;
  /**
   * 회사 직인이 등록돼 있는가 (2026-08-28 · 판 8).
   * ★ 옛 서버는 안 준다 → `undefined`. 그때는 지금처럼 「직인」 글자만 찍는다.
   */
  hasSeal?: boolean;
  /**
   * ★★★ **그 문서에 찍힌 직인의 주소** (2026-09-01 · 발급본 얼리기).
   *
   * 종전에는 화면이 `/institution/seal`(**지금** 회사 직인)을 불렀다. 그래서 회사가 직인을
   * 새로 등록하면 **이미 나간 증명서에도 도장이 찍혀** 나왔다. 이제 서버가 그 문서에 찍힌
   * 그림의 주소를 준다 — `null` 이면 **직인 없이 나간 종이**다.
   * ★ 옛 서버는 이 칸을 안 준다(`undefined`) → 화면이 종전대로 `hasSeal` 로 떨어진다.
   */
  sealSrc?: string | null;
}

export interface CreateCertificateInput {
  kind: CertKind;
  purpose?: string | null;
  recipient?: string | null;
  copies?: number;
  maskRrn?: boolean;
}

export async function createCertificate(input: CreateCertificateInput): Promise<CertificateDto> {
  const { data } = await apiClient.post<{ certificate: CertificateDto }>('/hr/certificates', input);
  return data.certificate;
}

/** 내 신청·발급 이력. 전 직원. */
export async function listMyCertificates(): Promise<CertificateDto[]> {
  const { data } = await apiClient.get<{ certificates: CertificateDto[] }>('/hr/certificates/mine');
  return data.certificates;
}

/** 회사 신청함 — 담당자(ADMIN)만. `pending` 은 대기 건수다. */
export async function listCertificates(status?: CertStatus): Promise<{ certificates: CertificateDto[]; pending: number }> {
  const { data } = await apiClient.get<{ certificates: CertificateDto[]; pending: number }>(
    '/hr/certificates', { params: status ? { status } : undefined },
  );
  return data;
}

export async function issueCertificate(id: string): Promise<CertificateDto> {
  const { data } = await apiClient.post<{ certificate: CertificateDto }>(`/hr/certificates/${id}/issue`, {});
  return data.certificate;
}

export async function rejectCertificate(id: string, reason: string): Promise<CertificateDto> {
  const { data } = await apiClient.post<{ certificate: CertificateDto }>(`/hr/certificates/${id}/reject`, { reason });
  return data.certificate;
}

/** 인쇄할 내용. 본인 또는 담당자만 — 아직 발급되지 않았으면 409. */
export async function getCertificateDocument(id: string): Promise<CertificateDocumentDto> {
  const { data } = await apiClient.get<{ document: CertificateDocumentDto }>(`/hr/certificates/${id}/document`);
  return data.document;
}
